import type { ColumnDef, Row, TableOptions } from '@tanstack/react-table';
import { flexRender, useReactTable } from '@tanstack/react-table';
import _ from 'lodash';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import {
	Fragment,
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { ButtonLoadMore } from '@adflow/frontend/ui-buttons';
import { ListPagination, ScrollShadowBox } from '@adflow/frontend/ui-layout';
import { theme } from '@adflow/frontend/ui-theme';
import type { AssignableRef } from '@adflow/frontend/util-common';
import {
	scrollIntoView,
	useDataTableCache,
	useDebounceTrue,
	useWindowViewWidth,
} from '@adflow/frontend/util-common';
import { TABLE_ROWS_COUNT_UNKNOWN } from '@adflow/frontend/util-router';
import type { DataTableItemType } from '@adflow/shared/util-column-templates';

import { ColumnTemplateButton } from '../column-template/ColumnTemplateButton';
import type { DataTableColumnProps } from '../column-template/types';
import { getSortByToggleProps } from '../getSortByToggleProps';
import type { DataTableOpenRowsProps, DataTableSortProps } from '../types';
import { useControlledTable } from '../useControlledTable';

import { ColumnPadding } from './ColumnPadding';
import { DataTableFullBody } from './DataTableFullBody';
import {
	getFlexCellStyle,
	getFlexRowStyle,
	getFlexTableStyle,
} from './flex-layout';
import type {
	DataTableFullOnRowClick,
	ExpandedRowRenderer,
	RowRendererProps,
} from './RowRenderer';
import { alternateBackground, RowRenderer } from './RowRenderer';
import { getStickyHeaderProps } from './sticky-layout';
import { styles, tableHeaderHeight } from './styles';

export interface DataTableFullBaseProps<T extends object>
	extends
		DataTableOpenRowsProps,
		DataTableSortProps,
		DataTableColumnProps<T>,
		Pick<RowRendererProps<T>, 'highlightStyle' | 'rowStyle'> {
	data: T[];
	itemType: DataTableItemType;
	tableWrapRef?: AssignableRef<HTMLDivElement | null>;
	/* NOTE: intended for when the parent item (usually the customer)
	   is archived, NOT for individual items in the table */
	archived?: boolean;
	hideColumnTemplateButton?: boolean;

	/** This needs to be wrapped in useCallback */
	onRowClick?: DataTableFullOnRowClick<T>;
	/** This needs to be wrapped in useCallback */
	expandedRowRender?: ExpandedRowRenderer<T>;
	/** This needs to be wrapped in useCallback */
	frontMatterRender?: () => ReactNode | null;
	/** This needs to be wrapped in useCallback */
	backMatterRender?: (rows: Row<T>[]) => ReactNode | null;
	/** This needs to be wrapped in useCallback */
	renderWhenDataEmpty?: () => ReactNode | null;
	isLoading?: boolean;
	isError?: boolean;
	renderOnError?: ReactNode | null;
	/**
	 * When data is loading, how long should we wait before showing the spinner?
	 */
	delaySpinnerMs?: number;
	setColumnsToExport?: (columns: ColumnDef<T>[]) => void;
	getRowId?: (data: T) => unknown;
	visualIndicator?: ReactNode;
	shouldScrollToTop?: boolean;
	minSticky?: number;
	rowHeight?: number;
	growLastColumn?: boolean;
}

export type DataTableFullPaginationProps =
	| {
			usePagination?: false;
			useInfiniteLoad?: false;
	  }
	| {
			usePagination: true;
			useInfiniteLoad?: false;

			currentPage: number;
			rowsPerPage: number;
			totalItems: number;
			exportButton?: ReactElement;
			tableFooter?: ReactElement;

			onChangePage: (newPage: number) => void;
			onChangeRowsPerPage: (newItemsPerPage: number | null) => void;
	  }
	| {
			usePagination?: false;
			useInfiniteLoad: true;

			hasMoreItems: boolean;
			fetchMoreItems: () => void;
	  };

export type DataTableFullProps<T extends object> = DataTableFullBaseProps<T> &
	DataTableFullPaginationProps;

const defaultColumn = {
	minSize: 30,
	size: 170,
	maxSize: 800,
} satisfies Partial<ColumnDef<unknown>>;

export function getNormalizedOpenRowId(rowId: string) {
	return /^-?\d+$/u.test(rowId) ? Number(rowId) : rowId;
}

export function isRowOpen(
	openRows: (string | number)[] | undefined,
	rowId: string
) {
	if (!openRows) {
		return false;
	}

	const normalizedRowId = getNormalizedOpenRowId(rowId);

	return (
		openRows.includes(rowId) ||
		(typeof normalizedRowId === 'number' && openRows.includes(normalizedRowId))
	);
}

/**
 * DataTableFull
 * @param expandedRowRender: render prop for rendering a row when it's been expanded
 * @param frontMatterRender: render prop for DOM nodes to display above the table header
 */
function DataTableFullImpl<T extends object>({
	delaySpinnerMs = 400,
	...props
}: DataTableFullProps<T>) {
	const showSpinner = Boolean(
		props.isLoading ||
		(props.usePagination && props.totalItems === TABLE_ROWS_COUNT_UNKNOWN)
	);

	const showError = Boolean(props.isError);
	const showSpinnerDebounced = useDebounceTrue(showSpinner, delaySpinnerMs);

	const dataCache = useDataTableCache({
		data: props.data,
		isLoading: showSpinner,
	});

	const getRowId = useCallback<Required<TableOptions<T>>['getRowId']>(
		(row, index) => {
			const getRowIdProp = props.getRowId;
			if (getRowIdProp) {
				return String(getRowIdProp(row));
			}
			return ('id' in row ? String(row.id) : undefined) || String(index);
		},
		[props.getRowId]
	);

	const table = useReactTable<T>({
		_features: [ColumnPadding],
		columns: props.columns,
		data: dataCache,
		defaultColumn,
		getRowId,

		...useControlledTable({
			...props,
			useColumnResizing: true,
		}),
	});

	const topRef = useRef(null);

	useEffect(() => {
		if (props.shouldScrollToTop) {
			scrollIntoView(topRef.current);
		}
	}, [props.shouldScrollToTop]);

	const windowWidth = useWindowViewWidth();
	const [tableWidth, setTableWidth] =
		useState<CSSProperties['maxWidth']>(windowWidth);

	const tableContainerRef = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		const container = tableContainerRef.current;
		if (container) {
			const width = container.scrollWidth || windowWidth;
			setTableWidth(width);
		}
	}, [windowWidth]);

	const headerGroups = table.getHeaderGroups();
	const growLastColumn = props.growLastColumn ?? true;
	// Setting alternative header background color to headerGroups
	const [coloredHeaderGroups, coloredColumnIds, stickyIndexes] = useMemo(() => {
		const coloredIndexes: number[] = [];
		const coloredColumnIds_: string[] = [];
		const stickyIndexes_: number[] = [];

		return [
			headerGroups.map((headerGroup) => ({
				...headerGroup,
				headers: headerGroup.headers.map((header, index) => {
					if (header.column.columnDef.meta?.sticky) {
						stickyIndexes_.push(index);
					}
					if (
						header.column.columns.length > 0 &&
						!coloredIndexes.includes(index - 1)
					) {
						coloredIndexes.push(index);

						// Getting column ids for the columns to be colored
						for (const column of header.column.columns) {
							coloredColumnIds_.push(column.id);
						}

						return {
							header,
							headerStyle: alternateBackground,
						};
					} else if (header.column.columns.length > 0) {
						return {
							header,
							headerStyle: { backgroundColor: theme.colors.background.default },
						};
					}
					return { header };
				}),
			})),
			coloredColumnIds_,
			stickyIndexes_,
		];
	}, [headerGroups]);

	const rows = table.getCenterRows();
	const firstExpandedRow = rows.find((row) =>
		isRowOpen(props.openRows, row.id)
	);

	return (
		<div
			style={{ ...getFlexTableStyle(table), width: '100%', height: '100%' }}
			data-boundary-table
		>
			<div
				css={styles.tableContainer}
				data-testid="data-table"
				ref={tableContainerRef}
			>
				{props.hideColumnTemplateButton ? null : (
					<ColumnTemplateButton
						{...props}
						// If we have more than 1 headerGroups put the config button in the bottom one
						topOffset={(headerGroups.length - 1) * tableHeaderHeight}
						hideColumnTemplateButton={props.hideColumnTemplateButton}
					/>
				)}
				<ScrollShadowBox
					role="table"
					outerStyle={{ height: '100%' }}
					scrollAreaStyle={styles.table}
					scrollAreaRef={props.tableWrapRef}
					topOffset={tableHeaderHeight * headerGroups.length}
				>
					<div css={[styles.headerCell, { position: 'absolute' }]}>
						{/**
						 * This div stays behind the header and does not scroll horizontally
						 * with the table. It is revealed by the rubber-band scrolling on iPad
						 * (where you can pull a scrollable element past the edge and it
						 * springs back into place), and it prevents the table from looking
						 * disconnected when that happens - see HUB-913. */}
					</div>
					<div
						ref={typeof props.shouldScrollToTop === 'boolean' ? topRef : null}
					/>

					<div css={styles.header}>
						{coloredHeaderGroups.map((headerGroup) => {
							return (
								<div
									key={headerGroup.id}
									role="row"
									style={getFlexRowStyle(table)}
								>
									{headerGroup.headers.map(({ header, headerStyle }, i) => {
										const isLastHeader = i === headerGroup.headers.length - 1;
										const flexCellStyle = getFlexCellStyle(
											header,
											isLastHeader,
											growLastColumn
										);
										const headerProps = _.merge(
											{ style: flexCellStyle },
											getStickyHeaderProps(header)
										);

										const emptyHeader = header.isPlaceholder;

										// @ts-expect-error `data-*` attributes are not in the type definition
										if (headerProps['data-sticky-cell']) {
											const stickyToLeft = stickyIndexes.indexOf(i);
											headerProps.style.zIndex =
												stickyIndexes.length - stickyToLeft + 1;
										}

										return (
											<Fragment key={header.id}>
												<div
													role="columnheader"
													{...headerProps}
													aria-colspan={header.colSpan}
													css={[
														!header.column.columnDef.meta?.disableOverflow &&
															styles.allCells,
														styles.headerCell,
														props.archived && styles.headerCellArchived,
														emptyHeader
															? {
																	backgroundColor:
																		theme.colors.background.default,
																}
															: undefined,
														headerStyle,
													]}
													data-testid="table-header"
												>
													<span
														{...getSortByToggleProps(
															header.column,
															props.setSort
														)}
														css={{ width: '100%' }}
													>
														{emptyHeader
															? null
															: flexRender(
																	header.column.columnDef.header,
																	header.getContext()
																)}
													</span>
													{header.column.getCanResize() ? (
														<div
															css={styles.resizerContainer(
																Boolean(
																	header.column.getIsPinned() ||
																	header.column.parent?.getIsPinned()
																),
																emptyHeader
															)}
															style={{ cursor: 'col-resize' }}
															onMouseDown={header.getResizeHandler()}
															onTouchStart={header.getResizeHandler()}
														>
															<div
																css={styles.resizer(
																	header.column.getIsResizing()
																)}
																id="resizer"
															/>
														</div>
													) : null}
												</div>
											</Fragment>
										);
									})}
								</div>
							);
						})}
					</div>

					<DataTableFullBody
						showSpinner={showSpinnerDebounced}
						showError={showError}
						renderOnError={props.renderOnError}
					>
						{/* Render out frontmatter. Consumer determines whatever styles are used. */}
						{props.frontMatterRender?.()}
						{rows.length > 0
							? rows.map((row) => {
									const isFirstExpanded = firstExpandedRow
										? firstExpandedRow.id === row.id
										: false;

									return (
										<RowRenderer
											key={row.id}
											itemType={props.itemType}
											isFirstExpanded={isFirstExpanded}
											row={row}
											table={table}
											coloredColumnIds={coloredColumnIds}
											isExpanded={isRowOpen(props.openRows, row.id)}
											toggleRowExpanded={() => {
												if (!props.setOpenRows || !props.openRows) return;
												if (props.openRows.includes(row.id)) {
													props.setOpenRows((rs) =>
														rs.filter((r) => r !== row.id)
													);
												} else {
													const normalizedRowId = getNormalizedOpenRowId(
														row.id
													);
													if (
														typeof normalizedRowId === 'number' &&
														props.openRows.includes(normalizedRowId)
													) {
														props.setOpenRows((rs) =>
															rs.filter((r) => r !== normalizedRowId)
														);
													} else {
														props.setOpenRows((rs) => [...rs, normalizedRowId]);
													}
												}
											}}
											onRowClick={props.onRowClick}
											expandedRowRender={props.expandedRowRender}
											tableWidth={tableWidth}
											archived={props.archived}
											columnWidths={table.getState().columnSizing}
											highlightStyle={props.highlightStyle}
											rowStyle={props.rowStyle}
											columnsTemplate={props.columns}
											rowHeight={props.rowHeight}
											growLastColumn={growLastColumn}
										/>
									);
								})
							: props.renderWhenDataEmpty?.()}
						{props.backMatterRender?.(rows)}
					</DataTableFullBody>
					{props.visualIndicator || null}
				</ScrollShadowBox>
			</div>
			{props.useInfiniteLoad && props.hasMoreItems ? (
				<div
					css={{
						marginTop: '0.5rem',
						marginBottom: '0.5rem',
						marginLeft: '1rem',
					}}
				>
					<ButtonLoadMore onClick={props.fetchMoreItems} />
				</div>
			) : null}
			{props.usePagination ? (
				<div>
					<ListPagination
						rowsPerPageOptions={[5, 10, 25, 50]}
						totalItems={props.totalItems}
						rowsPerPage={props.rowsPerPage}
						page={props.currentPage}
						onChangePage={props.onChangePage}
						onChangeRowsPerPage={props.onChangeRowsPerPage}
						itemType={props.itemType}
						tableFooter={props.tableFooter}
						exportButton={props.exportButton}
					/>
				</div>
			) : null}
		</div>
	);
}

export const DataTableFull = memo(
	DataTableFullImpl
) as typeof DataTableFullImpl;
